/**
 * Payer Intelligence -- analytics dashboard inside the workflow shell.
 *
 * Route: /PayerIntelligence
 *
 * This module is analytics-only: there is nothing to upload, so the
 * "Upload" step is marked Not Required in the stepper. The Choose step
 * lets the user pick which analytics view to render; Run pulls fresh data;
 * Results renders the tables and exposes per-table CSV downloads.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WorkflowLayout,
  ResultsPanel,
  Icon,
  getWorkflowModule,
} from '../../components/workflow';
import {
  getAnalyticsSummary,
  getContractVariance,
  getDenialsByPayer,
  getDenialsByCarc,
  getDrgHccReview,
  getLearningSignals,
  getTurnaround,
  getUnderpayment,
} from '../../services/suiteApi';
import { getSampleById } from '../../fixtures/testCases';
import './suite.css';

function fmt$(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtInt(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString();
}

function toCsv(rows, columns) {
  if (!rows || !rows.length) return '';
  const head = columns.map((c) => `"${c.label}"`).join(',');
  const body = rows.map((r) =>
    columns.map((c) => {
      const v = c.value(r);
      const s = v == null ? '' : String(v).replace(/"/g, '""');
      return `"${s}"`;
    }).join(',')
  ).join('\n');
  return `${head}\n${body}`;
}

function buildContext(view, data) {
  if (!data) return '';
  const lines = [`PAYER INTELLIGENCE — ${view}`];
  if (view === 'analytics') {
    const s = data.summary?.claims || {};
    const d = data.summary?.denials || {};
    lines.push(`Open denials: ${fmtInt(d.TotalDenials)}, denied amount: ${fmt$(d.TotalDeniedAmount)}, expected recovery: ${fmt$(d.TotalExpectedRecovery)}`);
    lines.push(`Claims filed: ${fmtInt(s.TotalClaims)}, paid: ${fmtInt(s.PaidClaims)}, total recovered: ${fmt$(s.TotalPaidAmount)}`);
    lines.push('');
    lines.push('Top payers (denials):');
    (data.denialsByPayer || []).slice(0, 5).forEach((r) => lines.push(`  - ${r.Payer}: ${fmtInt(r.DenialCount)} denials, ${fmt$(r.DeniedAmount)}`));
    lines.push('');
    lines.push('Top CARCs:');
    (data.byCarc || []).slice(0, 5).forEach((r) => lines.push(`  - ${r.Carc}: ${fmtInt(r.Count)} x ${fmt$(r.TotalAmount)}`));
  } else if (view === 'contract') {
    lines.push(`Contract recon findings: ${(data.contract || []).length}`);
  } else if (view === 'drg') {
    lines.push(`DRG findings: ${(data.drg?.drgFindings || []).length}, HCC findings: ${(data.drg?.hccFindings || []).length}`);
  }
  return lines.join('\n');
}

function Bar({ value, max }) {
  if (!max) return null;
  const pct = Math.max(2, Math.min(100, (Number(value) / Number(max)) * 100));
  return <div className="suite-bar"><div className="suite-bar__fill" style={{ width: `${pct}%` }} /></div>;
}

function DenialsByPayerTable({ rows }) {
  const max = Math.max(...(rows || []).map((r) => Number(r.DeniedAmount || 0)), 1);
  return (
    <table className="suite-table">
      <thead><tr><th>Payer</th><th>Denials</th><th>Appealed</th><th>Denied amount</th><th style={{ width: 180 }}>Share</th><th>Expected recovery</th></tr></thead>
      <tbody>
        {(rows || []).map((r) => (
          <tr key={`${r.PayerId}-${r.Payer}`}>
            <td><b>{r.Payer}</b></td>
            <td>{fmtInt(r.DenialCount)}</td>
            <td>{fmtInt(r.AppealedCount)}</td>
            <td>{fmt$(r.DeniedAmount)}</td>
            <td><Bar value={r.DeniedAmount} max={max} /></td>
            <td>{fmt$(r.ExpectedRecovery)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ByCarcTable({ rows }) {
  const max = Math.max(...(rows || []).map((r) => Number(r.TotalAmount || 0)), 1);
  return (
    <table className="suite-table">
      <thead><tr><th>CARC</th><th>Description</th><th>Count</th><th>Amount</th><th style={{ width: 180 }}>Share</th></tr></thead>
      <tbody>
        {(rows || []).slice(0, 20).map((r) => (
          <tr key={r.Carc}>
            <td><span className="suite-badge suite-badge--info">{r.Carc}</span></td>
            <td>{r.Description || '—'}</td>
            <td>{fmtInt(r.Count)}</td>
            <td>{fmt$(r.TotalAmount)}</td>
            <td><Bar value={r.TotalAmount} max={max} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TurnaroundTable({ rows }) {
  return (
    <table className="suite-table">
      <thead><tr><th>Payer</th><th>Claims measured</th><th>Avg days</th><th>Median</th><th>P90</th></tr></thead>
      <tbody>
        {(rows || []).map((r) => (
          <tr key={r.Payer}>
            <td><b>{r.Payer}</b></td>
            <td>{fmtInt(r.ClaimsMeasured)}</td>
            <td>{r.AvgDays ?? '—'}</td>
            <td>{r.MedianDays ?? '—'}</td>
            <td>{r.P90Days ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UnderpaymentTable({ rows }) {
  const max = Math.max(...(rows || []).map((r) => Number(r.TotalUnderpayment || 0)), 1);
  return (
    <table className="suite-table">
      <thead><tr><th>Payer</th><th>Underpaid claims</th><th>Total underpayment</th><th style={{ width: 180 }}>Share</th></tr></thead>
      <tbody>
        {(rows || []).map((r) => (
          <tr key={`${r.PayerId}-${r.Payer}`}>
            <td><b>{r.Payer}</b></td>
            <td>{fmtInt(r.UnderpaidClaims)}</td>
            <td>{fmt$(r.TotalUnderpayment)}</td>
            <td><Bar value={r.TotalUnderpayment} max={max} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const VIEWS = [
  { id: 'analytics', label: 'Payer + CARC analytics' },
  { id: 'contract',  label: 'Contract reconciliation' },
  { id: 'drg',       label: 'DRG / HCC review' },
];

export default function PayerIntelligencePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleDef = getWorkflowModule('payer-intelligence');

  const [view, setView] = useState(searchParams.get('view') || 'analytics');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const sampleId = searchParams.get('sample');

  const load = async (which = view) => {
    setBusy(true); setError(''); setData(null);
    try {
      if (which === 'analytics') {
        const [s, p, c, t, u, l] = await Promise.all([
          getAnalyticsSummary(),
          getDenialsByPayer(),
          getDenialsByCarc(),
          getTurnaround(),
          getUnderpayment(),
          getLearningSignals(),
        ]);
        setData({
          summary: s?.data || null,
          denialsByPayer: p?.data?.items || [],
          byCarc: c?.data?.items || [],
          turnaround: t?.data?.items || [],
          underpayment: u?.data?.items || [],
          signals: l?.data?.items || [],
        });
      } else if (which === 'contract') {
        const r = await getContractVariance();
        setData({ contract: r?.data?.items || [] });
      } else if (which === 'drg') {
        const r = await getDrgHccReview();
        setData({ drg: r?.data || {} });
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(view); }, [view]);
  useEffect(() => {
    if (!sampleId) return;
    const s = getSampleById(sampleId);
    if (s && s.moduleId === 'payer-intelligence') {
      const v = s.payload?.tab || 'analytics';
      setView(v);
      setSearchParams({ view: v });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleId]);

  const chatContext = useMemo(() => buildContext(view, data), [view, data]);
  const currentStep = data ? 'results' : 'run';

  // Build a CSV that mirrors the current view's primary table for download.
  const csv = useMemo(() => {
    if (!data) return null;
    if (view === 'analytics') {
      return toCsv(data.denialsByPayer, [
        { label: 'Payer', value: (r) => r.Payer },
        { label: 'Denials', value: (r) => r.DenialCount },
        { label: 'Appealed', value: (r) => r.AppealedCount },
        { label: 'DeniedAmount', value: (r) => r.DeniedAmount },
        { label: 'ExpectedRecovery', value: (r) => r.ExpectedRecovery },
      ]);
    }
    if (view === 'contract') {
      return toCsv(data.contract, [
        { label: 'Payer', value: (r) => r.payerName || r.payerId },
        { label: 'RemitsChecked', value: (r) => r.remitsChecked },
        { label: 'Underpayments', value: (r) => r.underpayments },
        { label: 'TotalUnderpayment', value: (r) => r.totalUnderpayment },
      ]);
    }
    if (view === 'drg') {
      return toCsv(data.drg?.drgFindings || [], [
        { label: 'Claim', value: (r) => r.claimRef },
        { label: 'SubmittedDRG', value: (r) => r.submittedDrg },
        { label: 'SuggestedDRG', value: (r) => r.suggestedDrg },
        { label: 'WeightDelta', value: (r) => r.weightDelta },
      ]);
    }
    return null;
  }, [view, data]);

  return (
    <WorkflowLayout
      module={moduleDef}
      currentStep={currentStep}
      skippedSteps={['upload']}
      caseContext={chatContext}
    >
      <section className="wf-step-card">
        <header className="wf-step-card__head">
          <h2 className="wf-step-card__title">
            <span className="wf-step-card__num">1</span>
            Pick an analytics view
          </h2>
        </header>
        <div className="wf-actions">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`wf-btn ${view === v.id ? '' : 'wf-btn--ghost'}`}
              onClick={() => { setView(v.id); setSearchParams({ view: v.id }); }}
            >
              {v.label}
            </button>
          ))}
          <button type="button" className="wf-btn wf-btn--ghost" disabled={busy} onClick={() => load(view)}>
            <Icon.play /> {busy ? 'Loading…' : moduleDef.runLabel}
          </button>
        </div>
        {error && <div className="wf-error">{error}</div>}
      </section>

      {data && (
        <ResultsPanel
          title={VIEWS.find((v) => v.id === view)?.label || 'Analytics'}
          data={data}
          downloadName={`payer-intelligence_${view}`}
          textReport={chatContext}
          csv={csv || undefined}
        >
          {view === 'analytics' && (
            <>
              <dl className="wf-kv">
                <div><dt>Open denials</dt><dd>{fmtInt(data.summary?.denials?.TotalDenials)}</dd></div>
                <div><dt>Denied amount</dt><dd>{fmt$(data.summary?.denials?.TotalDeniedAmount)}</dd></div>
                <div><dt>Claims filed</dt><dd>{fmtInt(data.summary?.claims?.TotalClaims)}</dd></div>
                <div><dt>Total recovered</dt><dd>{fmt$(data.summary?.claims?.TotalPaidAmount)}</dd></div>
              </dl>
              <div style={{ marginTop: 12 }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 14, color: 'var(--dm-ink)' }}>Denials by payer</h3>
                <DenialsByPayerTable rows={data.denialsByPayer} />
              </div>
              <div style={{ marginTop: 12 }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 14, color: 'var(--dm-ink)' }}>Denials by CARC</h3>
                <ByCarcTable rows={data.byCarc} />
              </div>
              <div style={{ marginTop: 12 }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 14, color: 'var(--dm-ink)' }}>Turnaround by payer</h3>
                <TurnaroundTable rows={data.turnaround} />
              </div>
              <div style={{ marginTop: 12 }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 14, color: 'var(--dm-ink)' }}>Underpayments by payer</h3>
                <UnderpaymentTable rows={data.underpayment} />
              </div>
            </>
          )}

          {view === 'contract' && (
            <>
              {(data.contract || []).length === 0 ? (
                <p className="wf-step-card__hint">No variance findings yet. Load a fee schedule via tools/contracts/ingest_fee_schedule.py for one of your payers.</p>
              ) : (
                <table className="suite-table">
                  <thead><tr><th>Payer</th><th>Remits</th><th>Underpaid lines</th><th>Silent PPO</th><th>RBP</th><th>Network mismatch</th><th>Total underpayment</th></tr></thead>
                  <tbody>
                    {(data.contract || []).map((r) => (
                      <tr key={r.payerId}>
                        <td><b>{r.payerName || r.payerId}</b></td>
                        <td>{fmtInt(r.remitsChecked)}</td>
                        <td>{fmtInt(r.underpayments)}</td>
                        <td>{fmtInt(r.silentPpoCount)}</td>
                        <td>{fmtInt(r.rbpCount)}</td>
                        <td>{fmtInt(r.networkMismatchCount)}</td>
                        <td>{fmt$(r.totalUnderpayment)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {view === 'drg' && (
            <>
              <h3 style={{ margin: '0 0 6px', fontSize: 14, color: 'var(--dm-ink)' }}>MS-DRG validation</h3>
              {(data.drg?.drgFindings || []).length === 0 ? (
                <p className="wf-step-card__hint">No institutional claims with DRG findings.</p>
              ) : (
                <table className="suite-table">
                  <thead><tr><th>Claim</th><th>Submitted DRG</th><th>Suggested DRG</th><th>Δ Weight</th><th>Reason</th></tr></thead>
                  <tbody>
                    {(data.drg?.drgFindings || []).map((d, i) => (
                      <tr key={i}>
                        <td>{d.claimRef}</td>
                        <td>{d.submittedDrg}</td>
                        <td><b>{d.suggestedDrg || '—'}</b></td>
                        <td>{d.weightDelta?.toFixed(3) ?? '—'}</td>
                        <td>{d.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <h3 style={{ margin: '14px 0 6px', fontSize: 14, color: 'var(--dm-ink)' }}>HCC capture review</h3>
              {(data.drg?.hccFindings || []).length === 0 ? (
                <p className="wf-step-card__hint">No HCC capture findings yet.</p>
              ) : (
                <table className="suite-table">
                  <thead><tr><th>Encounter</th><th>ICD</th><th>HCC</th><th>Coefficient</th><th>Status</th></tr></thead>
                  <tbody>
                    {(data.drg?.hccFindings || []).map((h, i) => (
                      <tr key={i}>
                        <td>{h.encounterRef}</td>
                        <td>{h.icd}</td>
                        <td>{h.hccCode}</td>
                        <td>{h.coefficient?.toFixed(2)}</td>
                        <td>{h.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </ResultsPanel>
      )}
    </WorkflowLayout>
  );
}
